/*
  Link public enquiry form.

  Contract: Link "Public enquiry integration guide" v3.0, 21 September 2026.

    on load    Turnstile challenges immediately -> token -> POST /public/token
               -> hold { sessionToken, expiresAt } in memory for this visitor
    on submit  POST /public/session/enquiry with Authorization: Bearer {sessionToken}
    on 201     POST any files to /public/session/enquiry/{uuid}/attachments
               straight away, then show the confirmation

  Turnstile hands its token to a callback, so it cannot be read at submit time —
  hence the handshake on load and a session held for the page's lifetime.

  The attachment window is about five minutes from the 201 and there is no
  add-files-later flow, so the upload follows the enquiry immediately rather
  than waiting for anything.
*/

const SELECTOR = '[data-public-enquiry]';
const WIDGET_LOAD_DEADLINE = 15000;
const RATE_LIMIT_COOLDOWN = 60000;

const NAME_MAX = 200;
// The API's own ceiling. A section may set a lower house limit per form.
const DESCRIPTION_MAX = 5000;
const PRODUCT_MAX = 100;
const SERIAL_MAX = 10;

const FILES_MAX = 5;
const FILE_BYTES_MAX = 10 * 1024 * 1024;

/*
  Extensions the API accepts. Photos and documents are matched by the browser's
  own type where it supplies one, but Link's own save files arrive with an empty
  or wrong type, so extension is the only reliable signal. Matched
  case-insensitively, per the guide.

  Video and audio are NOT accepted, whatever the form design shows.
*/
const ACCEPTED_EXTENSIONS = [
  // photos
  'jpg', 'jpeg', 'png', 'heic', 'heif', 'webp', 'avif', 'bmp',
  // documents
  'pdf', 'txt', 'csv', 'json', 'xml', 'rtf',
  // PCLink configurations, logs, layouts and G5 exports
  'pcl', 'pclr', 'pcl5', 'pclx',
  'llg', 'llg5', 'llgx',
  'llf', 'llf5', 'llfx',
  'lte', 'lcs', 'lcf',
  // PDM Link
  'pdmc', 'pdml', 'pdmlog',
  // DI Link
  'dic', 'dilog',
  // AIM Dash
  'zconfig', 'zconf2', 'xc1'
];

const REGIONS = [
  'americas',
  'new_zealand',
  'australia',
  'asia_pacific',
  'europe',
  'united_kingdom',
  'middle_east_africa'
];

const MESSAGES = {
  firstName: 'Enter your first name.',
  lastName: 'Enter your last name.',
  name: `Your name is longer than the ${NAME_MAX} characters we can send.`,
  email: 'Enter a valid email address.',
  region: 'Choose your region.',
  product: `Keep the product name to ${PRODUCT_MAX} characters.`,
  serialNumber: `Serial numbers are up to ${SERIAL_MAX} characters.`,
  description: (max) => `Enter your message, up to ${max} characters.`,
  filesTooMany: (count) => {
    const extra = count - FILES_MAX;
    return `You can attach up to ${FILES_MAX} files. Remove ${extra} ${extra === 1 ? 'file' : 'files'} to continue.`;
  },
  fileTooLarge: (name) => `${name} is over 10 MB. Please attach a smaller file.`,
  fileEmpty: (name) => `${name} is empty.`,
  fileType: (name) => `We cannot accept ${name}. Photos, documents and Link software files only.`,
  blocked:
    'The verification step could not load, so the form cannot be sent right now. Disable any ad or script blocker and reload, or contact us directly.',
  /*
    Covers two causes that the browser refuses to tell apart. A disallowed
    origin fails CORS preflight, so the API's own 403 never reaches this code —
    fetch rejects with an opaque network error, exactly as a real outage does.
    Promising the customer it is temporary would be false advice in the origin
    case, where retrying can never succeed.
  */
  unreachable:
    'We could not reach our enquiry service from this page. It may be a temporary problem, or this address may not be set up for the form yet. Please try again shortly, or contact us directly.',
  origin: 'This form is not enabled on this address. Please contact us directly.',
  generic: 'Something went wrong sending your enquiry. Please try again.',
  uncertain:
    'We could not confirm your enquiry was received. Your message has been kept below — please check with us before sending it again, so you do not raise it twice.',
  rateLimited: 'Too many attempts. Please wait a minute and try again.',
  attachmentsFailed:
    'Your enquiry was received, but we could not attach your files. Please reply to our confirmation with them, or raise a new enquiry with the files attached.'
};

function extensionOf(file) {
  const dot = file.name.lastIndexOf('.');
  return dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
}

class EnquiryForm {
  constructor(root) {
    this.root = root;
    this.form = root.querySelector('form');
    this.submit = root.querySelector('[data-enquiry-submit]');
    this.alert = root.querySelector('[data-enquiry-alert]');
    this.widget = root.querySelector('[data-enquiry-turnstile]');
    this.fileInput = root.querySelector('[data-enquiry-files]');
    this.fileList = root.querySelector('[data-enquiry-file-list]');
    this.api = root.dataset.apiBase.replace(/\/+$/, '');
    this.siteKey = root.dataset.siteKey;
    this.category = root.dataset.category;
    // Set per form by the Message block; never above the API's own ceiling.
    this.descriptionMax = Math.min(Number(root.dataset.descriptionMax) || DESCRIPTION_MAX, DESCRIPTION_MAX);

    this.session = null;
    this.widgetId = null;
    // distinguishes "the widget never ran" from "the handshake was refused";
    // the two need different advice and blaming an ad blocker for the latter
    // sends the customer down the wrong path
    this.tokenSeen = false;
    this.waiter = null;
    this.inFlight = false;
    // Each reason the submit button can be off, kept apart so clearing one
    // (say, removing a file) cannot switch it back on while another holds.
    this.coolingDown = false;
    this.failed = false;
    this.filesInvalid = false;

    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.onSubmit();
    });

    if (this.fileInput) {
      this.fileInput.addEventListener('change', () => this.renderFileList());
    }

    this.mountWidget();
  }

  /* ---------------- Turnstile ---------------- */

  mountWidget() {
    /*
      A blocked script produces no callback at all, so a deadline is the only
      way to detect it. Cloudflare's own scriptOptions.onError would replace the
      wrapper's load callback, which is why this is a timer instead.
    */
    this.loadDeadline = setTimeout(() => {
      if (this.session) return;
      if (this.tokenSeen) this.showAlert(MESSAGES.unreachable);
      else this.fail(MESSAGES.blocked);
    }, WIDGET_LOAD_DEADLINE);

    const render = () => {
      this.widgetId = window.turnstile.render(this.widget, {
        sitekey: this.siteKey,
        appearance: 'interaction-only',
        'refresh-expired': 'never',
        callback: (token) => {
          this.tokenSeen = true;
          this.exchange(token);
        },
        'error-callback': () => this.settle(null, 'Verification could not be completed.'),
        'unsupported-callback': () => this.settle(null, 'This browser cannot complete verification.'),
        'timeout-callback': () => this.settle(null, 'Verification expired unanswered.'),
        // Only re-challenge when no session is held; replacing a live session
        // would spend a request from the client rate-limit budget for nothing.
        'expired-callback': () => {
          if (!this.session) window.turnstile.reset(this.widgetId);
        }
      });
    };

    if (window.turnstile?.render) {
      render();
    } else {
      window.onloadTurnstileCallback = render;
      // the script may already be in flight with render=explicit
      const poll = setInterval(() => {
        if (window.turnstile?.render) {
          clearInterval(poll);
          render();
        }
      }, 200);
      setTimeout(() => clearInterval(poll), WIDGET_LOAD_DEADLINE);
    }
  }

  async exchange(token) {
    try {
      const response = await fetch(`${this.api}/public/token`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turnstileToken: token })
      });
      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        this.settle(null, payload?.errorCode ?? `token ${response.status}`);
        if (response.status === 403) this.fail(MESSAGES.origin);
        else this.showAlert(MESSAGES.unreachable);
        return;
      }

      clearTimeout(this.loadDeadline);
      this.session = payload;
      this.settle(payload);
    } catch (error) {
      // A refused preflight surfaces here as an opaque network error with no
      // API errorCode, so it cannot be told apart from the service being down.
      // The browser logs the CORS reason itself; this names the culprit so it
      // is obvious which call failed.
      console.error('[public-enquiry] token handshake failed', String(error));
      this.settle(null, String(error));
      this.showAlert(MESSAGES.unreachable);
    }
  }

  // Registration must come before reset(), or the fresh token arrives with
  // nobody waiting for it.
  refreshSession() {
    this.session = null;
    const pending = new Promise((resolve, reject) => {
      this.waiter = { resolve, reject };
    });
    window.turnstile.reset(this.widgetId);
    return pending;
  }

  settle(session, reason) {
    const waiter = this.waiter;
    this.waiter = null;
    if (!waiter) return;
    if (session) waiter.resolve(session);
    else waiter.reject(new Error(reason || 'verification failed'));
  }

  async getSession() {
    if (this.session && new Date(this.session.expiresAt) > new Date()) return this.session;
    if (this.waiter) return new Promise((resolve, reject) => { this.waiter = { resolve, reject }; });
    return this.refreshSession();
  }

  /* ---------------- files ---------------- */

  selectedFiles() {
    return this.fileInput ? Array.from(this.fileInput.files) : [];
  }

  renderFileList() {
    if (!this.fileList) return;
    const files = this.selectedFiles();
    this.fileList.innerHTML = '';
    files.forEach((file, index) => {
      const row = document.createElement('li');
      row.className = 'enquiry-file text-sm';

      const name = document.createElement('span');
      name.textContent = `${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)`;

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'enquiry-file__remove';
      remove.textContent = '×';
      remove.setAttribute('aria-label', `Remove ${file.name}`);
      remove.addEventListener('click', () => this.removeFile(index));

      row.append(name, remove);
      this.fileList.appendChild(row);
    });
    this.fileList.hidden = files.length === 0;
    // Re-run validation so a bad pick is named as soon as it is made rather
    // than at submit, when the customer has moved on.
    this.validateFiles();
  }

  /*
    A file input cannot drop a single file: picking again replaces the whole
    selection. So the list offers its own remove buttons, and the input's
    FileList is rebuilt without the removed file. FileList itself is
    read-only; a DataTransfer is the standard way to make a new one.
  */
  removeFile(index) {
    const kept = new DataTransfer();
    this.selectedFiles().forEach((file, i) => {
      if (i !== index) kept.items.add(file);
    });
    this.fileInput.files = kept.files;
    this.renderFileList();

    // Keep keyboard users in the list rather than dropping focus to the page.
    const buttons = this.fileList.querySelectorAll('.enquiry-file__remove');
    (buttons[Math.min(index, buttons.length - 1)] ?? this.fileInput).focus();
  }

  /*
    Mirrors the API's own rules so a bad file is named before it costs a
    request. The API is still the authority; this only saves a round trip.
  */
  validateFiles() {
    const files = this.selectedFiles();
    let message = null;

    if (files.length > FILES_MAX) {
      message = MESSAGES.filesTooMany(files.length);
    } else {
      const bad = files.find(
        (file) => file.size === 0 || file.size > FILE_BYTES_MAX || !ACCEPTED_EXTENSIONS.includes(extensionOf(file))
      );
      if (bad) {
        if (bad.size === 0) message = MESSAGES.fileEmpty(bad.name);
        else if (bad.size > FILE_BYTES_MAX) message = MESSAGES.fileTooLarge(bad.name);
        else message = MESSAGES.fileType(bad.name);
      }
    }

    this.setFieldError('files', message);
    // Off while the files are wrong, so a click cannot look like a hang:
    // the list's remove buttons are the way forward.
    this.filesInvalid = message !== null;
    this.syncSubmit();
    return message === null;
  }

  /* ---------------- validation ---------------- */

  setFieldError(field, message) {
    const el = this.root.querySelector(`[data-enquiry-error="${field}"]`);
    if (!el) return;
    el.textContent = message ?? '';
    el.hidden = !message;
  }

  // The API returns no field-level detail on a 422, so all field feedback is ours.
  validate() {
    const read = (field) => (this.form[field] ? this.form[field].value.trim() : '');

    const firstName = read('first_name');
    const lastName = read('last_name');
    const values = {
      category: this.category,
      name: [firstName, lastName].filter(Boolean).join(' '),
      email: read('email'),
      region: read('region')
    };

    const phone = read('phone');
    if (phone) values.phone = phone;

    const product = read('product');
    if (product) values.product = product;

    const serialNumber = read('serial_number');
    if (serialNumber) values.serialNumber = serialNumber;

    values.description = read('description');

    const errors = {};
    if (!firstName) errors.first_name = MESSAGES.firstName;
    if (this.form.last_name?.required && !lastName) errors.last_name = MESSAGES.lastName;
    if (values.name.length > NAME_MAX) errors.first_name = MESSAGES.name;
    if (!values.email || !this.form.email.checkValidity()) errors.email = MESSAGES.email;
    if (!REGIONS.includes(values.region)) errors.region = MESSAGES.region;
    if (product.length > PRODUCT_MAX) errors.product = MESSAGES.product;
    if (serialNumber.length > SERIAL_MAX) errors.serial_number = MESSAGES.serialNumber;
    if (!values.description || values.description.length > this.descriptionMax) {
      errors.description = MESSAGES.description(this.descriptionMax);
    }

    ['first_name', 'last_name', 'email', 'region', 'product', 'serial_number', 'description'].forEach(
      (field) => this.setFieldError(field, errors[field])
    );

    const filesOk = this.validateFiles();

    const first = Object.keys(errors)[0];
    if (first) this.form[first]?.focus();

    return Object.keys(errors).length === 0 && filesOk ? values : null;
  }

  /* ---------------- submit ---------------- */

  async onSubmit() {
    if (this.inFlight) return;

    const values = this.validate();
    if (!values) return;

    this.setBusy(true);
    this.showAlert(null);

    try {
      const receipt = await this.post(values);
      let attachmentsFailed = false;

      if (this.selectedFiles().length) {
        attachmentsFailed = !(await this.postAttachments(receipt.uuid));
      }

      this.succeed(receipt, attachmentsFailed);
    } catch (error) {
      this.handleFailure(error);
    } finally {
      this.setBusy(false);
    }
  }

  async post(values, isRetry = false) {
    const session = await this.getSession();
    const response = await fetch(`${this.api}/public/session/enquiry`, {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.sessionToken}`
      },
      body: JSON.stringify(values)
    });

    if (response.status === 201) return response.json();

    const payload = await response.json().catch(() => null);
    const code = payload?.errorCode;

    /*
      A rejected session means the enquiry was never recorded, so re-handshaking
      and sending once more is safe. Exactly one retry.
    */
    const sessionRejected =
      code === 'public/session-token-invalid' || code === 'public/session-token-expired';
    if (sessionRejected && !isRetry) {
      await this.refreshSession();
      return this.post(values, true);
    }

    const failure = new Error(payload?.message || `enquiry ${response.status}`);
    failure.status = response.status;
    failure.code = code;
    failure.requestId = payload?.requestId;
    throw failure;
  }

  /*
    Returns true when every file was stored. A failure here does not undo the
    enquiry, which is already recorded, so it never throws into the enquiry's
    own error handling — the customer is told the message arrived and the files
    did not.

    Content-Type is deliberately unset: the browser has to add the multipart
    boundary itself.
  */
  async postAttachments(uuid) {
    const body = new FormData();
    this.selectedFiles().forEach((file) => body.append('files', file, file.name));

    try {
      const session = await this.getSession();
      const response = await fetch(`${this.api}/public/session/enquiry/${uuid}/attachments`, {
        method: 'POST',
        credentials: 'omit',
        headers: { Authorization: `Bearer ${session.sessionToken}` },
        body
      });

      if (response.status === 201) return true;

      const payload = await response.json().catch(() => null);
      console.error('[public-enquiry] attachments', response.status, payload?.errorCode ?? '', payload?.requestId ?? '');
      return false;
    } catch (error) {
      console.error('[public-enquiry] attachments', String(error));
      return false;
    }
  }

  succeed({ uuid, createdAt }, attachmentsFailed) {
    this.receipt = { uuid, createdAt };

    const template = this.root.querySelector('template[data-enquiry-success]');
    const confirmation = document.createElement('div');
    if (template) confirmation.appendChild(template.content.cloneNode(true));

    if (attachmentsFailed) {
      const warning = document.createElement('p');
      warning.className = 'text-sm';
      warning.setAttribute('role', 'alert');
      warning.textContent = MESSAGES.attachmentsFailed;
      confirmation.appendChild(warning);
    }

    const reference = document.createElement('p');
    reference.className = 'text-sm text-opacity';
    reference.textContent = `Reference: ${uuid}`;
    confirmation.appendChild(reference);

    this.form.replaceWith(confirmation);
  }

  handleFailure(error) {
    /*
      A timeout, a dropped connection or a 500 may still have recorded the
      enquiry — the endpoint has no idempotency key. Never resubmit these
      automatically; keep the form and let the customer decide.
    */
    if (!error.status || error.status >= 500) {
      this.showAlert(MESSAGES.uncertain, 'info');
      return;
    }

    if (error.status === 429) {
      this.showAlert(MESSAGES.rateLimited, 'error');
      this.cooldown();
      return;
    }

    if (error.code === 'public/disallowed-origin') {
      this.fail(MESSAGES.origin);
      return;
    }

    // 422 should be unreachable — our validation mirrors the documented limits.
    // If it fires, the contract has moved; say so rather than looping.
    this.showAlert(MESSAGES.generic, 'error');
    console.error('[public-enquiry]', error.status, error.code, error.requestId ?? '');
  }

  /* ---------------- ui ---------------- */

  cooldown() {
    this.coolingDown = true;
    this.syncSubmit();
    setTimeout(
      () => {
        this.coolingDown = false;
        this.syncSubmit();
      },
      RATE_LIMIT_COOLDOWN + Math.floor(Math.random() * 15000)
    );
  }

  setBusy(busy) {
    this.inFlight = busy;
    this.syncSubmit();
    this.submit.setAttribute('aria-busy', String(busy));
  }

  syncSubmit() {
    this.submit.disabled = this.inFlight || this.coolingDown || this.failed || this.filesInvalid;
  }

  showAlert(message, status = 'error') {
    if (!message) {
      this.alert.hidden = true;
      this.alert.textContent = '';
      return;
    }
    this.alert.innerHTML = '';
    const box = document.createElement('div');
    box.className = `alert alert--${status} text-sm font-medium leading-tight`;
    box.setAttribute('role', 'alert');
    box.textContent = message;
    this.alert.appendChild(box);
    this.alert.hidden = false;
  }

  // Only for conditions that cannot recover on a retry: a blocked script and a
  // refused origin. Anything transient leaves the button live so that
  // submitting re-runs the handshake.
  fail(message) {
    this.showAlert(message, 'error');
    this.failed = true;
    this.syncSubmit();
  }
}

const initialised = new WeakSet();

function init(root) {
  if (initialised.has(root)) return;
  initialised.add(root);
  new EnquiryForm(root);
}

document.querySelectorAll(SELECTOR).forEach(init);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.(SELECTOR).forEach(init);
});
